"use client";

import * as SelectPrimitive from "@radix-ui/react-select";
import { useTranslations } from "next-intl";
import { default as React } from "react";

import { type SearchPassengers } from "@trip/constants";
import { useDateRangeFormatter } from "@trip/react-hooks";
import { absoluteToCalendarDate, cn, type DateRange } from "@trip/utils";

import { Calendar } from "~/app/[locale]/(default)/_components/calendar";
import type { SearchResultsData } from "~/app/[locale]/(default)/search/page";
import { SearchIcon } from "~/components/icons/search-icon";
import { Passengers, usePassengerTriggerLabel } from "~/components/passengers";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { Select, SelectValue } from "~/components/ui/select";

import { WithSearchFocus } from "./search-focus";
import { useSearchForm } from "./use-search-form";

function Who({
  value,
  hasFocus,
  onFocus,
  onChange,
}: {
  hasFocus: boolean;
  onChange: (value: SearchPassengers) => void;
  onFocus: () => void;
  value: SearchPassengers;
}) {
  const t = useTranslations("Search");
  const passengerTriggerLabel = usePassengerTriggerLabel();
  return (
    <Popover>
      <PopoverTrigger asChild aria-label={t("who")}>
        <button
          type="button"
          className="basis-[220px]"
          onFocus={() => {
            onFocus();
          }}
        >
          <WithSearchFocus hasFocus={hasFocus}>
            <div className="text-xs font-medium">{t("who")}</div>
            <div className="min-w-0 truncate text-sm md:text-base">
              {passengerTriggerLabel(value)}
            </div>
          </WithSearchFocus>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="shadow-card-search w-auto rounded-3xl border-none"
        sideOffset={8}
      >
        <fieldset className="grid grid-cols-[1fr_max-content] items-center gap-2">
          <Passengers onChange={onChange} value={value} />
        </fieldset>
      </PopoverContent>
    </Popover>
  );
}

const WhereTrigger = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
  ref?: React.Ref<React.ComponentRef<typeof SelectPrimitive.Trigger>>;
}) => {
  return (
    <SelectPrimitive.Trigger className={cn("", className)} ref={ref} {...props}>
      {children}
    </SelectPrimitive.Trigger>
  );
};
WhereTrigger.displayName = SelectPrimitive.Trigger.displayName;

const WhereContent = ({
  className,
  children,
  position = "popper",
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
  ref?: React.Ref<React.ComponentRef<typeof SelectPrimitive.Content>>;
}) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      className={cn(
        "shadow-card-search animate-in fade-in-80 relative z-50 max-h-[22rem] min-w-32 overflow-hidden rounded-3xl bg-white p-6",
        position === "popper" && "translate-y-2",
        className,
      )}
      position={position}
      ref={ref}
      {...props}
    >
      <div className="bg-linear-to-b absolute inset-x-0 bottom-0 h-6 from-transparent to-white" />
      <SelectPrimitive.Viewport
        className={cn(
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
        <div className="h-6 w-full" />
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
);
WhereContent.displayName = SelectPrimitive.Content.displayName;

const WhereItem = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  ref?: React.Ref<React.ComponentRef<typeof SelectPrimitive.Item>>;
}) => (
  <SelectPrimitive.Item
    className={cn(
      "outline-hidden data-checked:text-prune-500 data-disabled:pointer-events-none data-disabled:opacity-50 focus:bg-prune-200 focus:text-prune-500 relative flex w-full cursor-default select-none items-center rounded-lg p-3 text-sm leading-none",
      className,
    )}
    ref={ref}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
);
WhereItem.displayName = SelectPrimitive.Item.displayName;

export function Search({
  locations,
  location,
  dateRange,
  passengers,
  tourType,
}: {
  dateRange?: DateRange | { end: number; start: number };
  location?: string;
  locations: SearchResultsData["allLocations"];
  passengers?: SearchPassengers;
  tourType?: string;
}) {
  const [focusField, setFocusField] = React.useState<
    "location" | "calendar" | "passengers"
  >("location");
  const { fields, submit } = useSearchForm({
    dateRange: dateRange
      ? {
          end:
            typeof dateRange.end === "number"
              ? absoluteToCalendarDate(dateRange.end)
              : dateRange.end,
          start:
            typeof dateRange.start === "number"
              ? absoluteToCalendarDate(dateRange.start)
              : dateRange.start,
        }
      : dateRange,
    location: location ?? undefined,
    passengers,
    tourType,
  });

  const formatter = useDateRangeFormatter({ dateStyle: "medium" });

  const t = useTranslations("Search");
  const tCommon = useTranslations("Common");

  return (
    <form
      className="shadow-card-search flex h-20 w-full justify-between gap-2 rounded-full bg-neutral-200"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Select
        onOpenChange={(open) => {
          if (open) {
            setFocusField("location");
          }
        }}
        onValueChange={(value) => {
          fields.location.onChange(value === "__all" ? "" : value);
        }}
        value={fields.location.value ?? "__all"}
      >
        <SelectPrimitive.Trigger
          aria-label={t("where")}
          className={"shrink-0 basis-[120px] md:basis-[180px]"}
        >
          <WithSearchFocus hasFocus={focusField === "location"}>
            <div className="text-xs font-medium">{t("where")}</div>
            <div className="min-w-0 text-sm md:text-base">
              <SelectValue placeholder={t("allOfJapan")} />
            </div>
          </WithSearchFocus>
        </SelectPrimitive.Trigger>
        <WhereContent>
          <div className="grid grid-cols-2 gap-x-1 *:max-w-[140px] *:truncate md:grid-cols-3 lg:grid-cols-4">
            <WhereItem key={null} value={"__all"}>
              {t("allOfJapan")}
            </WhereItem>
            {locations.map(({ name, slug }) => (
              <WhereItem key={slug} value={slug}>
                {name}
              </WhereItem>
            ))}
          </div>
        </WhereContent>
      </Select>
      <Calendar
        hasFocus={focusField === "calendar"}
        onChange={(value) => {
          fields.dateRange.onChange(value);
        }}
        onFocus={() => {
          setFocusField("calendar");
        }}
        value={fields.dateRange.value}
      >
        <div className="min-w-0 max-w-full truncate pr-8 text-sm md:text-base">
          {fields.dateRange.value ? (
            <>{formatter(fields.dateRange.value)}</>
          ) : (
            t("addDates")
          )}
        </div>
      </Calendar>
      <Who
        hasFocus={focusField === "passengers"}
        onChange={fields.passengers.onChange}
        onFocus={() => {
          setFocusField("passengers");
        }}
        value={fields.passengers.value as unknown as SearchPassengers}
      />
      <div className="flex aspect-square h-full items-center justify-center">
        <button
          className="shadow-button-emboss hover:shadow-button-emboss-hover rounded-full p-[14px] transition-all [background-image:var(--sunset-left-right)] hover:[background-image:var(--sunset-hover)]"
          type="submit"
          aria-label={tCommon("submit")}
        >
          <SearchIcon className="size-8 text-white" />
          <span className="sr-only">{tCommon("submit")}</span>
        </button>
      </div>
    </form>
  );
}
